import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Baby,
  BedDouble,
  Camera,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
  Sparkles,
  UserRound,
  Wind,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/buccal-fat-removal-mumbai";

const BUCCAL_FAQS = [
  {
    question: "Will buccal fat removal make my face look too hollow as I age?",
    answer:
      "This is the most important risk to understand. Facial fat naturally depletes with age. Patients who have buccal fat removal in their 20s and 30s when they have genuine buccal fat prominence get a defined result that holds well. Patients who are already thin or who undergo removal in middle age risk an excessively hollow appearance as natural aging reduces surrounding fat over subsequent decades. Dr. Jain assesses this directly during consultation.",
  },
  {
    question: "Is buccal fat removal permanent?",
    answer:
      "Yes. The buccal fat pad does not regenerate once removed. The result is permanent. Natural facial aging continues after surgery, as it does with any procedure, but the specific fat deposit that caused the fullness does not return.",
  },
  {
    question: "Will there be visible scars after surgery?",
    answer:
      "No. The incision is made inside the mouth against the inner cheek surface. There are no external incisions and no visible scars.",
  },
  {
    question: "How long does recovery take?",
    answer:
      "Most patients return to desk work within 5 to 7 days. A soft diet is recommended for the first week. Final results are visible at 1 to 3 months as all swelling resolves.",
  },
  {
    question: "Can buccal fat removal be combined with other procedures?",
    answer:
      "Yes, and this is common for patients who want comprehensive facial contouring. Frequent combinations include chin augmentation for lower face definition, submental liposuction for double chin reduction, and rhinoplasty for patients who want complete facial balance improvement in a single surgery.",
  },
  {
    question: "Will buccal fat removal work if I lose more weight?",
    answer:
      "Weight loss does not selectively reduce buccal fat. Patients who are lean with persistently full cheeks have a structural concern that diet and exercise do not address. Buccal lipectomy targets this specific fat deposit regardless of overall body weight.",
  },
  {
    question: "How is buccal fat removal different from liposuction of the face?",
    answer:
      "Buccal fat removal removes a specific anatomical fat pad located deep in the cheek through an internal incision. Facial liposuction addresses subcutaneous fat under the chin or along the jawline through a different technique. The two procedures target different fat compartments and are sometimes combined for patients with concerns in both areas.",
  },
  {
    question: "At what age should I consider buccal fat removal?",
    answer:
      "Most surgeons recommend waiting until the mid-20s when facial structure has fully matured and buccal fat has settled into its adult volume. Operating before facial maturity risks overcorrection. There is no strict upper age limit, but patients over 40 are assessed more carefully for skin elasticity and natural facial volume before proceeding.",
  },
];

const buccalStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SurgicalProcedure",
      name: "Buccal fat removal (buccal lipectomy)",
      alternateName: ["Buccal lipectomy", "Cheek fat removal"],
      description:
        "Surgical removal of the buccal fat pad through an intraoral incision for slimmer lower cheeks and improved facial contour. Permanent reduction of this deep fat compartment; patient selection critical to avoid excessive hollowing with age.",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Cheek",
      },
      followup:
        "Days 1–3: mild intraoral swelling; soft diet. Days 5–7: return to desk work; swelling decreasing. Weeks 2–3: contour more defined. Months 1–3: final result as swelling resolves.",
      howPerformed:
        "Local anesthesia with optional light sedation; intraoral incision parallel to upper molars; buccal fat pad exposed and partial removal; dissolvable sutures; 30–60 minutes; same-day discharge.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        { "@type": "PropertyValue", name: "Anesthesia", value: "Local anesthesia with or without light sedation" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: BUCCAL_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function BuccalFatJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buccalStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Buccal Fat Removal", href: "/buccal-fat-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Buccal Fat Removal
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Facial Contouring Surgery by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Buccal Fat Removal" icon="◇" size="hero"  imageSrc={HERO.buccalFatRemoval} imageAlt="Buccal fat removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Buccal Fat Removal Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Buccal fat removal, clinically called buccal lipectomy, removes the buccal fat pad from the lower cheeks to
              create a slimmer, more angular facial contour. The buccal fat pad sits deep within the cheek, separate from
              the subcutaneous fat that changes with body weight. This is why some patients have full, round cheeks
              regardless of how lean the rest of their body is. Diet and exercise do not selectively reduce this fat.
            </p>
            <p>
              The procedure takes 30 to 60 minutes under local anesthesia. The incision is made inside the mouth, leaving
              no external scars. The fat pad is accessed through the incision, a controlled amount is removed, and the site
              is closed with dissolvable sutures. Patients go home the same day.
            </p>
            <p>
              Dr. Vicky Jain performs buccal fat removal for patients who want a more defined lower face. The
              result is sharper cheekbones, a more visible jaw angle, and a face that photographs with better facial
              structure. The change is permanent because the removed fat does not regenerate.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Buccal Fat Removal
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Patient selection is the most important decision in buccal fat removal. The procedure works well for
                patients between 20 and 40 with good skin elasticity who have genuine buccal fat prominence. It is not
                appropriate for patients who are very thin, have naturally low facial volume, or are older, where existing
                facial fat is already depleting with age. Removing buccal fat from the wrong patient produces a hollow,
                gaunt result that worsens over time.
              </p>
              <p>
                Dr. Jain assesses the buccal fat contribution to your cheek fullness against your overall facial structure
                before agreeing to operate. The amount of fat removed is calibrated to produce definition without
                overcorrection. The difference between a sculpted result and a skeletonized one is the precision of that
                judgment. Patients who are borderline candidates are told directly, and an alternative approach is
                discussed.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Round or Full Cheeks",
      icon: UserRound,
      copy: "The buccal fat pad creates fullness in the lower cheek that is genetically determined and does not reduce with weight loss. Removal addresses this specific fat deposit directly.",
    },
    {
      title: "Lack of Cheekbone Definition",
      icon: Sparkles,
      copy: "Prominent buccal fat obscures the natural cheekbone structure. Removing it allows the underlying bone to read clearly, creating the angular definition that photos and lighting respond to.",
    },
    {
      title: "Blurred Jaw Angle",
      icon: Wind,
      copy: "Fullness in the lower cheek softens the transition from cheek to jaw. Reducing this volume sharpens the jaw angle and improves the lower face silhouette.",
    },
    {
      title: "Round Facial Profile in Photos",
      icon: Camera,
      copy: "Full cheeks photograph as two-dimensional. Buccal fat removal creates the shadow and contour that gives the face structure and definition in photos and on camera.",
    },
    {
      title: "Baby Face Appearance",
      icon: Baby,
      copy: "Some patients have a persistently youthful, round facial structure well into adulthood due to prominent buccal fat. Removal shifts the face toward a more defined, adult appearance.",
    },
    {
      title: "Cheek Fullness That Resists Weight Loss",
      icon: Scale,
      copy: "When a patient's face stays round despite being lean elsewhere, the cause is structural, not dietary. Buccal lipectomy is the only targeted correction for this specific fat deposit.",
    },
  ];

  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="buccal-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">What Buccal Fat Removal Corrects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`buccal-corrects-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COMPARISON_ROWS = [
  {
    label: "Target Area",
    surgery: "Removes buccal fat pad specifically.",
    weightLoss: "Reduces general body fat, not specific facial deposits.",
    fillers: "Adds shadow effect through volume placement, does not remove fat.",
  },
  {
    label: "Permanence",
    surgery: "Permanent. Removed fat does not return.",
    weightLoss: "Variable, does not selectively reduce buccal fat.",
    fillers: "Temporary, requires repeat sessions every 9 to 12 months.",
  },
  {
    label: "Result Precision",
    surgery: "High. Fat removal is controlled and anatomy-specific.",
    weightLoss: "Low for facial contouring.",
    fillers: "Moderate.",
  },
  {
    label: "Downtime",
    surgery: "3 to 7 days.",
    weightLoss: "None.",
    fillers: "Minimal.",
  },
  {
    label: "Appropriate For",
    surgery: "Patients with genuine buccal fat prominence and good skin elasticity.",
    weightLoss: "General health improvement.",
    fillers: "Patients who want temporary contouring without surgery.",
  },
];

function VsAlternativesSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="buccal-vs-alternatives-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Buccal Fat Removal vs. Other Contouring Options
        </h2>
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <div className="hidden lg:grid lg:grid-cols-4 gap-0 border border-[#E8E2D9] bg-[#E8E2D9] min-w-[720px]">
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 font-medium" />
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight">Buccal fat removal</div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center">Weight loss</div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight">Fillers / contouring</div>
            {COMPARISON_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <div className="bg-white p-3 text-sm font-medium text-deep-navy border-t border-[#E8E2D9]">{row.label}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.surgery}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.weightLoss}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.fillers}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-4">
            {COMPARISON_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9] md:col-span-2">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Buccal fat removal</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.surgery}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Weight loss</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.weightLoss}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Fillers / contouring</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.fillers}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden space-y-4">
            {COMPARISON_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9]">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Buccal fat removal</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.surgery}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Weight loss</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.weightLoss}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Fillers / contouring</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.fillers}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-deep-navy/60 text-center mt-8 max-w-3xl mx-auto leading-relaxed">
            Non-surgical facial contouring with fillers adds the illusion of definition by placing volume strategically. It
            does not remove the fat causing the fullness. Surgery is the only permanent solution for buccal fat prominence.
          </p>
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Buccal Fat Removal is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 30 to 60 minutes under local anesthesia with or without light sedation. No general anesthesia
              is required. Dr. Jain makes a small incision inside the mouth against the inner cheek surface, parallel to the
              upper molars. No external incision is made at any point.
            </p>
            <p>
              The buccal fat pad is gently exposed through the incision. The fat is carefully separated from the surrounding
              tissue and a controlled amount is removed. The quantity removed is assessed during surgery based on the fat
              pad size and the degree of contouring required. The incision is closed with dissolvable sutures that do not
              require removal.
            </p>
            <p>
              Patients go home within a few hours of the procedure. A soft diet, antiseptic mouthwash, and standard oral
              hygiene are the main post-operative requirements. No dressings or external wound care are needed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling inside the cheeks and slight discomfort when opening the mouth widely. Liquid and soft foods recommended. Normal for the face to look fuller during this period due to swelling.",
      icon: BedDouble,
    },
    {
      period: "Days 5 to 7",
      description:
        "Most patients return to desk work and normal daily activity. Swelling reduces noticeably. The cheeks look less full than before surgery even through residual swelling.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Significant improvement in cheek contour is visible. The face reads as more defined. Residual internal swelling continues to resolve.",
      icon: Dumbbell,
    },
    {
      period: "Months 1 to 3",
      description:
        "Final sculpted result is visible. The full extent of cheekbone definition and jaw angle improvement is apparent once all swelling has resolved.",
      icon: Layers,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">
              Who Is a Good Candidate for Buccal Fat Removal?
            </h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              The best candidates are adults between 20 and 40 at a stable, healthy weight with good skin elasticity and
              genuine buccal fat prominence. Skin elasticity matters because after fat removal, the skin needs to contract
              and conform to the new contour. Patients with significantly reduced skin elasticity may not get a clean
              result from fat removal alone.
            </p>
            <p>
              Patients who are very thin or already have low facial volume are not good candidates. Buccal fat naturally
              reduces with age, so removing it in a patient who already has a lean face accelerates the hollow, gaunt
              appearance that develops in older age. Patients over 45 are assessed with particular care for this reason. The
              right candidate has a specific structural problem: prominent buccal fat that makes the face rounder than its
              underlying bone structure warrants. If that is your concern and your skin quality supports the procedure,
              buccal fat removal produces a significant, permanent improvement.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Buccal" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {BUCCAL_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Buccal Fat Removal Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain evaluates your buccal fat prominence, skin elasticity, facial volume, and
          age-related factors to determine whether the procedure is appropriate for your anatomy. If you are a good
          candidate, you leave with a clear surgical plan. If you are not, Dr. Jain will tell you directly and recommend
          what will actually produce the result you are looking for.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Chin Augmentation",
      description: "Define the lower face and jaw angle alongside cheek contouring for a complete result.",
      href: "/chin-augmentation-in-mumbai",
    },
    {
      name: "Rhinoplasty",
      description: "Balance nasal proportion with a more angular, defined facial structure.",
      href: "/rhinoplasty-in-mumbai",
    },
    {
      name: "Facelift",
      description: "For patients with facial laxity alongside volume concerns who need structural lifting rather than fat removal.",
      href: "/facelift-surgery-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BuccalFatRemovalPage() {
  return (
    <>
      <SEO
        title="Buccal Fat Removal | Dr. Vicky Jain"
        description="Full cheeks that don't respond to diet or exercise need targeted correction. Dr. Vicky Jain performs buccal fat removal with no visible scars. Book today."
        canonical="/buccal-fat-removal-mumbai"
      />
      <BuccalFatJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <VsAlternativesSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
